import { useState, useEffect } from "react";
import { Calendar, CalendarIcon, Euro, FileText, Settings, Star, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DateCalendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { statusDisplayMap, statusColors } from "@/constants/projectMeta";
import { cn } from "@/lib/utils";
import { getProjectTrackStatus } from "@/utils/projectTrackStatus";
import { MultiSelectProducts } from "@/components/invoices/MultiSelectProducts";
import { MultiSelectProposals } from "@/components/ui/multi-select-proposals";

interface ProjectOverviewTabProps {
  project: any;
  clients: any[];
  proposals: any[];
  isEditingInline: boolean;
  onEditingChange: (editing: boolean) => void;
}

export function ProjectOverviewTab({ project, clients, proposals, isEditingInline, onEditingChange }: ProjectOverviewTabProps) {
  const [formData, setFormData] = useState({
    name: project.name || "",
    client_id: project.client_id || "",
    project_number: project.project_number || "",
    status: project.status || "delivered",
    confirm_date: project.confirm_date || null,
    start_date: project.start_date || null,
    delivered_date: project.delivered_date || null,
    promised_delivery_date: project.promised_delivery_date || null,
    proposal_id: project.proposal_id || "none",
    proposal_ids: project.proposal_ids || [],
    tools: project.tools || [],
    sold_for: project.sold_for || project.value || "",
    extra_value: project.extra_value || "",
    notes: project.notes || "",
    arr: project.arr || false,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Update form data when project changes
  useEffect(() => {
    console.log("DEBUG Overview: Project changed", { 
      project: project?.name, 
      tools: project?.tools, 
      toolsType: typeof project?.tools,
      toolsLength: project?.tools?.length 
    });
    
    setFormData({
      name: project.name || "",
      client_id: project.client_id || "",
      project_number: project.project_number || "",
      status: project.status || "delivered",
      confirm_date: project.confirm_date || null,
      start_date: project.start_date || null,
      delivered_date: project.delivered_date || null,
      promised_delivery_date: project.promised_delivery_date || null,
      proposal_id: project.proposal_id || "none",
      proposal_ids: project.proposal_ids || [],
      tools: project.tools || [],
      sold_for: project.sold_for || project.value || "",
      extra_value: project.extra_value || "",
      notes: project.notes || "",
      arr: project.arr || false,
    });
  }, [project]);

  // NOTE: Auto-sync removed - users can set project value independently of proposals

  // Fetch products for tools selection
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, base_price")
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch invoices for this project to calculate invoiced percentage
  const { data: projectInvoices = [] } = useQuery({
    queryKey: ["project-invoices", project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("amount, status, invoice_number")
        .eq("project_id", project.id)
        .neq("status", "draft"); // Only include issued invoices, not drafts
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!project.id,
  });

  const updateProjectMutation = useMutation({
    mutationFn: async (updatedData: any) => {
      const { data, error } = await supabase
        .from("projects")
        .update(updatedData)
        .eq("id", project.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onEditingChange(false);
      toast({
        title: "Success",
        description: "Project updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to update project: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deriveToolsFromProposals = (proposalIds: string[]) => {
    const proposalTools = proposalIds.flatMap((id) => {
      const proposal = proposals.find((p) => p.id === id);
      return Array.isArray(proposal?.tools) ? proposal.tools : [];
    });
    return [...new Set(proposalTools)];
  };

  const handleSave = () => {
    const derivedTools = deriveToolsFromProposals(formData.proposal_ids || []);

    const dataToUpdate = {
      ...formData,
      name: formData.name,
      proposal_id: formData.proposal_ids?.length > 0 ? formData.proposal_ids[0] : null,
      proposal_ids: formData.proposal_ids || [],
      sold_for: formData.sold_for === "" ? null : Math.round(parseFloat(formData.sold_for) * 100) / 100 || null,
      extra_value: formData.extra_value === "" ? null : Math.round(parseFloat(formData.extra_value) * 100) / 100 || null,
      confirm_date: formData.confirm_date || null,
      start_date: formData.start_date || null,
      delivered_date: formData.delivered_date || null,
      promised_delivery_date: formData.promised_delivery_date || null,
      // Always sync tools from linked proposals when any proposals are linked
      tools: derivedTools.length > 0 ? derivedTools : (formData.tools || []),
    };

    updateProjectMutation.mutate(dataToUpdate);
  };

  const handleCancel = () => {
    setFormData({
      name: project.name || "",
      client_id: project.client_id || "",
      project_number: project.project_number || "",
      status: project.status || "delivered",
      confirm_date: project.confirm_date || null,
      start_date: project.start_date || null,
      delivered_date: project.delivered_date || null,
      promised_delivery_date: project.promised_delivery_date || null,
      proposal_id: project.proposal_id || "none",
      proposal_ids: project.proposal_ids || [],
      tools: project.tools || [],
      sold_for: project.sold_for || project.value || "",
      extra_value: project.extra_value || "",
      notes: project.notes || "",
      arr: project.arr || false,
    });
    onEditingChange(false);
  };

  // Filter proposals by selected client
  const clientProposals = proposals.filter(p => p.client_id === formData.client_id);

  // Calculate profitability using project.sold_for or fallback to project.value
  const soldFor = parseFloat(project.sold_for) || parseFloat(project.value) || 0;
  
  // Calculate actual cost from the actual_costs JSON structure
  const calculateActualCostTotal = (actualCosts: any): number => {
    if (!actualCosts) return 0;
    
    if (actualCosts.toolCosts && typeof actualCosts.toolCosts === 'object') {
      // Multiple tools structure
      const toolCosts = actualCosts.toolCosts as { [key: string]: { external_costs?: number; internal_costs?: number } };
      return Object.values(toolCosts).reduce((sum: number, tool) => {
        const externalCosts = parseFloat(String(tool?.external_costs)) || 0;
        const internalCosts = parseFloat(String(tool?.internal_costs)) || 0;
        return sum + externalCosts + internalCosts;
      }, 0);
    } else {
      // Single tool structure
      return (parseFloat(String(actualCosts.external_costs)) || 0) + (parseFloat(String(actualCosts.internal_costs)) || 0);
    }
  };
  
  const actualCost = calculateActualCostTotal(project.actual_costs);
  const profitability = soldFor > 0 ? ((soldFor - actualCost) / soldFor * 100) : 0;

  // Calculate invoiced percentage based on actual invoice data
  const totalInvoicedAmount = projectInvoices.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0);
  const invoicedPercentage = soldFor > 0 ? (totalInvoicedAmount / soldFor * 100) : 0;

  // Calculate Extra Value invoiced percentage
  // Using consumption model: invoices first cover Initial Value, then Extra Value
  const extraValue = parseFloat(project.extra_value) || 0;
  const amountOverInitialValue = Math.max(0, totalInvoicedAmount - soldFor);
  const extraValueInvoicedPercentage = extraValue > 0 ? Math.min(100, (amountOverInitialValue / extraValue) * 100) : 0;

  return (
    <div className="space-y-6">
      {isEditingInline && (
        <div className="flex items-center justify-end space-x-2 mb-4">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateProjectMutation.isPending}>
            {updateProjectMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Basic Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="name">Project Name</Label>
                  <Input
                    id="name"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter project name"
                    className="w-full"
                  />
                </>
              ) : (
                <p className="text-sm">
                  Project Name: {project.name && (
                    <span className="font-bold">{project.name}</span>
                  )}
                </p>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="client">Client</Label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <p className="text-sm">
                  Client: {formData.client_id && clients.find(c => c.id === formData.client_id) && (
                    <span className="font-bold">{clients.find(c => c.id === formData.client_id)?.name}</span>
                  )}
                </p>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="delivered">Ended</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <div className="flex items-center text-sm">
                  Status: <Badge 
                    className={`ml-2 ${statusColors[formData.status as keyof typeof statusColors]}`}
                    variant="secondary"
                  >
                    {statusDisplayMap[formData.status as keyof typeof statusDisplayMap]}
                  </Badge>
                </div>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="proposal">Linked Proposals</Label>
                    <MultiSelectProposals
                      proposals={clientProposals}
                      selectedProposalIds={formData.proposal_ids || []}
                      onSelectionChange={(ids) => {
                        const uniqueTools = deriveToolsFromProposals(ids);
                        setFormData({
                          ...formData,
                          proposal_ids: ids,
                          tools: uniqueTools.length > 0 ? uniqueTools : (formData.tools || []),
                        });
                      }}
                      placeholder="Select proposals (optional)"
                      clientFilter={formData.client_id}
                    />
                </>
              ) : (
                <div className="text-sm">
                  <span>Linked Proposals: </span>
                  {formData.proposal_ids && formData.proposal_ids.length > 0 ? (
                    <span className="font-bold">
                      {formData.proposal_ids
                        .map(id => proposals.find(p => p.id === id)?.title)
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )}
                </div>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="project_number">Project Number</Label>
                  <Input
                    id="project_number"
                    value={formData.project_number}
                    onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
                    placeholder="Will be auto-generated when status = Confirm"
                  />
                </>
              ) : (
                <p className="text-sm">
                  Project Number: {formData.project_number && (
                    <span className="font-bold font-mono">{formData.project_number}</span>
                  )}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Dates
              </CardTitle>
              {(() => {
                const trackStatus = getProjectTrackStatus({
                  status: formData.status,
                  start_date: formData.start_date,
                  delivered_date: formData.delivered_date,
                  promised_delivery_date: formData.promised_delivery_date
                });
                
                if (!trackStatus) return null;
                
                return (
                  <Badge variant="outline" className={trackStatus === 'on_track' ? "text-green-600 border-green-600" : "text-red-600 border-red-600"}>
                    {trackStatus === 'on_track' ? "On Track" : "Off Track"}
                  </Badge>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              {isEditingInline ? (
                <>
                  <Label>Confirm Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !formData.confirm_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.confirm_date ? format(new Date(formData.confirm_date), "dd/MM/yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <DateCalendar
                        mode="single"
                        selected={formData.confirm_date ? new Date(formData.confirm_date) : undefined}
                        onSelect={(date) => setFormData({ ...formData, confirm_date: date ? format(date, 'yyyy-MM-dd') : null })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </>
              ) : (
                <p className="text-sm">
                  Confirm Date: {formData.confirm_date && (
                    <span className="font-bold">{format(new Date(formData.confirm_date), "dd/MM/yyyy")}</span>
                  )}
                </p>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !formData.start_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.start_date ? format(new Date(formData.start_date), "dd/MM/yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <DateCalendar
                        mode="single"
                        selected={formData.start_date ? new Date(formData.start_date) : undefined}
                        onSelect={(date) => setFormData({ ...formData, start_date: date ? format(date, 'yyyy-MM-dd') : null })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </>
              ) : (
                <p className="text-sm">
                  Start Date: {formData.start_date && (
                    <span className="font-bold">{format(new Date(formData.start_date), "dd/MM/yyyy")}</span>
                  )}
                </p>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !formData.delivered_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.delivered_date ? format(new Date(formData.delivered_date), "dd/MM/yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <DateCalendar
                        mode="single"
                        selected={formData.delivered_date ? new Date(formData.delivered_date) : undefined}
                        onSelect={(date) => setFormData({ ...formData, delivered_date: date ? format(date, 'yyyy-MM-dd') : null })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </>
              ) : (
                <p className="text-sm">
                  End Date: {formData.delivered_date && (
                    <span className="font-bold">{format(new Date(formData.delivered_date), "dd/MM/yyyy")}</span>
                  )}
                </p>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="promised_delivery">Promised Delivery</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.promised_delivery_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.promised_delivery_date ? format(new Date(formData.promised_delivery_date), "dd/MM/yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <DateCalendar
                        mode="single"
                        selected={formData.promised_delivery_date ? new Date(formData.promised_delivery_date) : undefined}
                        onSelect={(date) => setFormData({ ...formData, promised_delivery_date: date ? format(date, 'yyyy-MM-dd') : null })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </>
              ) : (
                <p className="text-sm">
                  Promised Delivery: {formData.promised_delivery_date && (
                    <span className="font-bold">{format(new Date(formData.promised_delivery_date), "dd/MM/yyyy")}</span>
                  )}
                </p>
              )}
            </div>

            <div>
              {isEditingInline ? (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="arr"
                    checked={formData.arr || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, arr: checked })}
                  />
                  <Label htmlFor="arr">ARR Project</Label>
                </div>
              ) : (
                <p className="text-sm">
                  ARR Project: {formData.arr ? (
                    <span className="font-bold text-green-600">Yes</span>
                  ) : (
                    <span className="text-muted-foreground">No</span>
                  )}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Price & Profit */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Euro className="h-5 w-5 mr-2" />
              Tools & Profit
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm">
                Tools: {(() => {
                  console.log("DEBUG Overview Tools Display - CURRENT STATE:", {
                    projectName: project?.name,
                    clientName: project?.clients?.name || "No client data",
                    formDataTools: formData.tools,
                    projectTools: project?.tools,
                    toolsType: typeof formData.tools,
                    toolsLength: formData.tools?.length,
                    products: products?.length,
                    toolsArray: Array.isArray(formData.tools),
                    rawFormData: JSON.stringify(formData.tools),
                    rawProjectTools: JSON.stringify(project?.tools)
                  });
                  
                  // Force use project.tools instead of formData.tools if they differ
                  const toolsToUse = project?.tools || formData.tools || [];
                  console.log("DEBUG Tools to use:", toolsToUse);
                  
                  if (toolsToUse && toolsToUse.length > 0) {
                    const toolNames = toolsToUse.map(toolId => {
                      const product = products.find(p => p.id === toolId);
                      console.log("DEBUG Tool lookup:", { toolId, product: product?.name });
                      return product?.name;
                    }).filter(Boolean);
                    
                    console.log("DEBUG Tool names found:", toolNames);
                    
                    if (toolNames.length > 0) {
                      return (
                        <span className="font-bold text-blue-600">
                          {toolNames.join(", ")}
                        </span>
                      );
                    }
                  }
                  
                  return <span className="text-muted-foreground">No tools selected</span>;
                })()}
              </p>
            </div>

            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="sold_for">Initial Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    id="sold_for"
                    value={formData.sold_for || ""}
                    onChange={(e) => setFormData({ ...formData, sold_for: e.target.value })}
                    placeholder="0.00"
                    className="w-full"
                  />
                </>
              ) : (
                <p className="text-sm">
                  Initial Value: {soldFor > 0 && (
                    <span className="font-bold">€{soldFor.toLocaleString()}</span>
                  )}
                </p>
              )}
            </div>

            {!isEditingInline && (
              <div>
                <p className="text-sm">
                  Invoiced so far: <span className="font-bold">{invoicedPercentage.toFixed(1)}%</span>
                </p>
              </div>
            )}

            <div>
              {isEditingInline ? (
                <>
                  <Label htmlFor="extra_value">Extra Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    id="extra_value"
                    value={formData.extra_value || ""}
                    onChange={(e) => setFormData({ ...formData, extra_value: e.target.value })}
                    placeholder="0.00"
                    className="w-full"
                  />
                </>
              ) : (
                <p className="text-sm">
                  Extra Value: {(parseFloat(project.extra_value) || 0) > 0 && (
                    <span className="font-bold">€{parseFloat(project.extra_value).toLocaleString()}</span>
                  )}
                </p>
              )}
            </div>

            {!isEditingInline && (parseFloat(project.extra_value) || 0) > 0 && (
              <div>
                <p className="text-sm">
                  Invoiced so far: <span className="font-bold">{extraValueInvoicedPercentage.toFixed(1)}%</span>
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center space-x-2 text-sm">
                Profitability: {soldFor > 0 && (
                  <Badge variant={profitability > 0 ? "default" : "destructive"}>
                    {profitability.toFixed(1)}%
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditingInline ? (
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add project notes..."
                rows={4}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {project.notes}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}